import { z } from 'zod'
import { ReservationF } from '../../types/reservation-detail.types'
import { TFunction } from 'i18next'
import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useCustomError } from 'src/utils/custom-errors.hook'
import { useForm, SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

export default function ReferenceFormHook({ reservation, onSubmit }: { reservation: ReservationF; onSubmit: (data: ReferenceFormSchema) => Promise<void> }) {
  const { t } = useTranslation()
  const referenceSchemaWithTranslation = getReferenceFormSchema(t)

  const customErrorMap = useCustomError()
  z.setErrorMap(customErrorMap)
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<ReferenceFormSchema>({ resolver: zodResolver(referenceSchemaWithTranslation) })

  const submitHandler: SubmitHandler<ReferenceFormSchema> = useCallback(
    async (data: ReferenceFormSchema) => {
      await onSubmit(data)
    },
    [onSubmit],
  )
  const onFormSubmit = useCallback(handleSubmit(submitHandler), [handleSubmit, submitHandler])

  useEffect(() => {
    setValue('mainGuestId', reservation.reference?.mainGuest?.id)
    setValue('customerPersonId', reservation.reference?.customerPerson?.id ?? null)
    setValue('customerCompanyId', reservation.reference?.customerCompany?.id ?? null)
    setValue('agencyId', reservation.reference?.agency?.id ?? null)
  }, [])

  return { onFormSubmit, control, errors }
}

function getReferenceFormSchema(t?: TFunction) {
  return z.object({
    mainGuestId: z.string().refine((data) => (data ? true : false), { message: t?.('validation.required_error') }),
    customerPersonId: z.string().nullable(),
    customerCompanyId: z.string().nullable(),
    agencyId: z.string().nullable(),
  })
}

const referenceFormSchema = getReferenceFormSchema()
export type ReferenceFormSchema = z.infer<typeof referenceFormSchema>
